/**
 * PortalFeaturesSection Component
 * Showcases the key features of the Talk Australia Client Portal.
 * Appears in midmarket and enterprise tiers.
 */

import React, { useState } from 'react';

const FEATURES = [
  {
    icon: '\u{1F4CA}',
    title: 'Real-Time Dashboard',
    description: 'Monitor all your services, usage, and spend from a single pane of glass. Live status for every connection.',
  },
  {
    icon: '\u{1F4DE}',
    title: 'Support Ticketing',
    description: 'Lodge and track support tickets 24/7. See real-time updates, escalation paths, and resolution timelines.',
  },
  {
    icon: '\u{1F4C4}',
    title: 'Invoice & Billing',
    description: 'View invoices, download statements, and manage payment methods. One consolidated bill for all services.',
  },
  {
    icon: '\u{1F465}',
    title: 'User Management',
    description: 'Add or remove users, assign permissions, and manage mobile devices all from the portal.',
  },
  {
    icon: '\u{1F4F1}',
    title: 'Mobile App',
    description: 'Full portal access on iOS and Android. Lodge tickets, check status, and manage services on the go.',
  },
  {
    icon: '\u{1F4E6}',
    title: 'Service Provisioning',
    description: 'Request new services, upgrades, or moves. Track provisioning progress with estimated completion dates.',
  },
];

const styles = {
  section: {
    padding: '60px 20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 16px',
    backgroundColor: 'rgba(0, 87, 255, 0.15)',
    color: '#5B9DFF',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#8B92A5',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: '#1A2332',
    borderRadius: '12px',
    padding: '28px',
    border: '1px solid #2A3444',
    transition: 'all 0.3s ease',
  },
  cardHover: {
    borderColor: '#0057FF',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 87, 255, 0.1)',
  },
  icon: {
    fontSize: '36px',
    marginBottom: '16px',
    display: 'block',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#8B92A5',
    lineHeight: '1.6',
  },
};

function PortalFeaturesSection({ prospect }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <div style={styles.badge}>Your Client Portal</div>
        <h2 style={styles.title}>Everything You Need, One Login</h2>
        <p style={styles.subtitle}>
          Once onboarded, {prospect?.companyName || 'your team'} gets full access to the Talk Australia
          Client Portal — your central hub for managing all services.
        </p>
      </div>

      <div style={styles.grid}>
        {FEATURES.map((feature, index) => (
          <div
            key={index}
            style={{
              ...styles.card,
              ...(hoveredCard === index ? styles.cardHover : {}),
            }}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <span style={styles.icon}>{feature.icon}</span>
            <h3 style={styles.cardTitle}>{feature.title}</h3>
            <p style={styles.cardDescription}>{feature.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PortalFeaturesSection;
